<template>
  <div class="totp-container">
    <div class="row">
            <div class="totp-input-group col-12">
    <input 
      v-for="(digit, index) in digits" :key="index"
      :ref="el => inputRefs[index] = el"
      maxlength="1"
      required
      v-model="digits[index]"
      @input="onInput(index, $event)"
      @keydown="onKeyDown(index, $event)"
      @paste="onPaste"
      class="totp-input text-center" outlined dense
      :class="{ 'error-border': hasError }"
      :disable="isLoading"
      />
      </div>
      <div class="col-sm-1" v-if="$slots.prependInput">
        <slot name="prependInput"></slot>
      </div>
      <div class="col-sm-1" v-if="prependIcon? true: false">
        <q-icon :name="prependIcon"></q-icon>
      </div>
      <div class="col text-center">
        <q-btn
          :loading="isLoading"
          :disable="!isCodeComplete || isLoading"
          color="primary"
          unelevated
          @click="verifyCode"
          class="q-px-xl"
          :icon="mdiCheckCircle"
        >
        </q-btn>
      </div>
    </div>
  </div>
</template>

<script>

import {mdiCheckCircle } from '@quasar/extras/mdi-v7'; 
import { ref, reactive, computed, nextTick, onMounted } from 'vue'

export default {
  name: "OtpInput",
  data: () => ({
    otp: [], 
    activeIdx: 0,
  }),
  props: {
    label: String,
    prependIcon: String,
    icon: String,
    length: {
      type: [Number, String],
      default: 6
    },
    type: String,
    modelValue: String,
  },
  emits: ['code-completed', 'finish'],
setup(props, { emit }) {
    const digits = reactive(Array(6).fill(''))
    const inputRefs = ref([])
    const isLoading = ref(false)
    const hasError = ref(false)
    const errorMessage = ref('')

    const isCodeComplete = computed(() => {
      return digits.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const fullCode = computed(() => {
      return digits.join('')
    })

    const onInput = (index, event) => {
      const value = event.target.value
      
      // Ne garder que les chiffres
      if (value && !/^\d$/.test(value)) {
        digits[index] = ''
        return
      }

      hasError.value = false
      digits[index] = value

      // Passer au champ suivant si un chiffre est saisi
      console.log(value);
      if (value && index < 5) {
        nextTick(() => {
          inputRefs.value[index + 1]?.focus()
        })
      }

      // Émettre l'événement si le code est complet
      if (isCodeComplete.value) {
        emit('code-complete', fullCode.value)
      }
    }

    const onKeyDown = (index, event) => {
      const key = event.key

      if (key === 'Backspace') {
        if (digits[index] === '' && index > 0) {
          // Si le champ est vide, aller au champ précédent
          nextTick(() => {
            inputRefs.value[index - 1]?.focus()
          })
        } else {
          // Effacer le chiffre actuel
          digits[index] = ''
        }
      } else if (key === 'ArrowLeft' && index > 0) {
        nextTick(() => {
          inputRefs.value[index - 1]?.focus()
        })
      } else if (key === 'ArrowRight' && index < 5) {
        nextTick(() => {
          inputRefs.value[index + 1]?.focus()
        })
      } else if (key === 'Enter' && isCodeComplete.value) {
        verifyCode()
      }
    }

    const onPaste = (event) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      
      // Extraire seulement les chiffres
      const numbers = pastedData.replace(/\D/g, '')
      
      if (numbers.length >= 6) {
        for (let i = 0; i < 6; i++) {
          digits[i] = numbers[i] || ''
        }
        
        // Mettre le focus sur le dernier champ rempli
        nextTick(() => {
          const lastFilledIndex = digits.findLastIndex(d => d !== '')
          if (lastFilledIndex >= 0 && lastFilledIndex < 5) {
            inputRefs.value[lastFilledIndex + 1]?.focus()
          }
        })

        if (isCodeComplete.value) {
          emit('code-complete', fullCode.value)
        }
      }
    }

    const clearCode = () => {
      for (let i = 0; i < 6; i++) {
        digits[i] = ''
      }
      hasError.value = false
      errorMessage.value = ''
      
      nextTick(() => {
        inputRefs.value[0]?.focus()
      })
    }

    const verifyCode = async () => {
      if (!isCodeComplete.value) return

      isLoading.value = true
      hasError.value = false

      emit('finish', fullCode.value)      
    }

    onMounted(() => {
      // Mettre le focus sur le premier champ au montage
      nextTick(() => {
        inputRefs.value[0]?.focus()
      })
    })

    return {
      digits,
      inputRefs,
      isLoading,
      hasError,
      errorMessage,
      isCodeComplete,
      fullCode,
      onInput,
      onKeyDown,
      onPaste,
      clearCode,
      verifyCode,
      mdiCheckCircle: mdiCheckCircle
    }
  }, 
  methods: {
    onError(errorMessage) {
      console.log("received on error");
      var self = this;
      self.isLoading = false;
      self.hasError = true;
      self.errorMessage = errorMessage;
    }

  }
}
</script>

<style scoped>
.totp-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 1.5rem;
}

.totp-input-group {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
}
.totp-input input {
  width: 2em;
  padding:0.5em;
  text-align:center;
  border-radius: 5px;
  margin: 2px;
  border: 2px solid #55525c;
  font-weight: bold;
  outline: none;
  transition: all 0.1s;
}

.totp-input input:focus {
  border: 2px solid #a527ff;
  box-shadow: 0 0 2px 2px #a527ff6a;
}

.totp-input {
  width: 2rem;
  height: 2.5rem;
}

.totp-input :deep(.q-field__control) {
  height: 2.5rem;
}

.totp-input :deep(.q-field__native) {
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

.error-border :deep(.q-field__control) {
  border-color: #c10015 !important;
}

.totp-input :deep(.q-field__control):focus-within {
  border-color: #1976d2;
  border-width: 2px;
}

@media (max-width: 600px) {
  .totp-input {
    width: 2rem;
    height: 2.5rem;
  }
  
  .totp-input :deep(.q-field__control) {
    height: 0.75rem;
  }
  
  .totp-input-group {
    gap: 0.25rem;
  }
}
</style>
